import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { useMutationWithError, usePaginateQuery } from "@/hooks";
import { adminApi } from "@/lib/api";
import { type Paginate, type SearchRequest } from "@/types";

import { type AdminUnit } from "@/modules/chart/admin/types";

export type UnitList = Paginate<AdminUnit<true, true, true, true, true>>;

export const units = createQueryKeys("admin-chart-units", {
  all: (page?: number, filter?: SearchRequest) => [{ page, filter }],
  one: (id?: number) => [id],
});

const include = "users,unit_level,directorPosition.{users},parent";

export const fetchUnitsQueryOptions = (
  page: number = 0,
  search?: SearchRequest,
) =>
  queryOptions({
    ...units.all(page, search),
    queryFn: () =>
      adminApi
        .get<UnitList>("/chart/units", {
          params: { include, page, ...search },
        })
        .then((response) => response.data),
  });

export const useFetchUnits = (search?: SearchRequest) =>
  usePaginateQuery((page) => fetchUnitsQueryOptions(page, search));

export const fetchUnitQueryOptions = (id?: number) =>
  queryOptions({
    ...units.one(id),
    queryFn: () =>
      adminApi
        .get<AdminUnit<true, true, true, true, true>>(`/chart/units/${id}`, {
          params: { include },
        })
        .then((response) => response.data),
    enabled: id !== undefined,
  });

export const useFetchUnit = (id?: number) =>
  useQuery(fetchUnitQueryOptions(id));

export interface UnitRequest {
  code: number;
  name: string;
  chart_unit_level_id?: number | null;
  type: "official" | "operational";
  director_position_id?: number | null;
  employee_limit?: number | null;
  description?: string | null;
  note?: string | null;
  parent_id?: number | null;
}

export const useCreateUnit = () => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (data: UnitRequest) =>
      adminApi.post("/chart/units/", data).then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: units.all._def,
      });
    },
  });
};

export const useUpdateUnit = (id: number) => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (data: UnitRequest) =>
      adminApi
        .put(`/chart/units/${id}`, data)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: units.all._def,
      });
      queryClient.invalidateQueries(units.one(id));
    },
  });
};

export const useCreateOrUpdateUnit = (id: number | undefined) => {
  const create = useCreateUnit();
  const update = useUpdateUnit(id!);
  return id ? update : create;
};

export const useDeleteUnit = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => adminApi.delete(`chart/units/${id}`),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({
        queryKey: units.all._def,
      });
      queryClient.invalidateQueries(units.one(id));
    },
  });
};
